/**
 * One export implementation, built on the field-registry idea from
 * `admin/utils/catalogue.ts`. The legacy app wrote `escapeCsvField` three separate
 * times and assembled its rows inline on each page, so the deposits CSV and the
 * catalogue CSV disagreed about dates, money, and how to quote a comma.
 */

export interface ExportField<TRow> {
    /** Stable id, used as the selection key. */
    key: string
    /** Column header in the file. */
    label: string
    /**
     * Return a primitive. Money should be formatted with `formatMoney` (a bare
     * number) rather than `formatCurrency`, so the cell stays numeric in Excel.
     */
    value: (row: TRow) => string | number | boolean | null | undefined
    /** Off by default in the dialog. For the wide, rarely wanted columns. */
    optional?: boolean
    /** Shown under the label in the dialog when the column needs explaining. */
    hint?: string
}

/**
 * RFC 4180 quoting.
 *
 * A leading `=`, `+`, `-` or `@` is prefixed with a tab, because Excel and Sheets
 * treat those cells as formulas. Card names and operator-entered notes reach these
 * files, so this is a live injection path, not a theoretical one.
 *
 * A cell reading exactly `ID` is quoted. Excel reads a file that opens with those two
 * characters as SYLK and refuses it outright; SheetJS quotes them for that reason, so
 * the legacy deposit and transaction files carry the quotes and these match them.
 */
export function escapeCsvField(value: string | number | boolean | null | undefined): string {
    if (value === null || value === undefined) return ''

    let text = String(value)
    if (/^[=+\-@\t\r]/.test(text)) text = `\t${text}`
    if (text === 'ID') return '"ID"'
    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`
    return text
}

export function buildCsv<TRow>(rows: TRow[], fields: ExportField<TRow>[]): string {
    const lines = [fields.map(field => escapeCsvField(field.label)).join(',')]
    for (const row of rows) {
        lines.push(fields.map(field => escapeCsvField(field.value(row))).join(','))
    }
    // CRLF and a BOM: without them Excel on Windows mis-detects the encoding and
    // mangles every card name with an accent.
    return `\ufeff${lines.join('\r\n')}\r\n`
}

export function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.rel = 'noopener'
    document.body.append(link)
    link.click()
    link.remove()
    // Revoking immediately can cancel the download in Safari.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export function downloadCsv<TRow>(rows: TRow[], fields: ExportField<TRow>[], filename: string) {
    downloadBlob(new Blob([buildCsv(rows, fields)], { type: 'text/csv;charset=utf-8' }), filename)
}

/**
 * `xlsx` is ~400 KB, so it is imported only when someone actually picks the format.
 * Route-level splitting alone would not help: the deposits page would still pull it
 * in for everyone who never exports.
 */
export async function downloadXlsx<TRow>(
    rows: TRow[],
    fields: ExportField<TRow>[],
    filename: string,
    sheetName = 'Export'
) {
    const { utils, write } = await import('xlsx')

    const matrix = [fields.map(field => field.label), ...rows.map(row => fields.map(field => field.value(row) ?? ''))]

    const sheet = utils.aoa_to_sheet(matrix)
    sheet['!cols'] = fields.map(field => ({ wch: Math.min(40, Math.max(10, field.label.length + 4)) }))

    const book = utils.book_new()
    // Excel rejects sheet names past 31 characters or containing []:*?/\
    utils.book_append_sheet(book, sheet, sheetName.slice(0, 31).replace(/[[\]:*?/\\]/g, ' '))

    const buffer = write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer
    downloadBlob(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        filename
    )
}

/** `deposits-2026-09-07.csv` -- sortable, and unambiguous in a downloads folder. */
export function exportFilename(base: string, extension: 'csv' | 'xlsx'): string {
    const stamp = new Date().toISOString().slice(0, 10)
    return `${base}-${stamp}.${extension}`
}
